import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Select } from '@/_components';
import { useGetInStockInventoriesMutation } from '@/_pages/inventories/shared';
import { useGetInventoryKitWithItemsOnlyMutation } from '@/_pages/settings/inventory-kits/shared';
import type { InventoryQueryLinkerSchema } from '@/_pages/settings/inventory-kits/shared/schemas/inventory';
import type { InventoryKitQuerySchema } from '@/_pages/settings/inventory-kits/shared/schemas/inventory-kit';
import { useBoolean, useDebounce } from '@/_shared';

import { type InventoryAndKitGroupedOption, type InventoryAndKitOptionType, isInventory, isKit } from './helper';
import { usePickTicketsItemFormContext } from './use-inventory-kit-item-context';

import type { InventoryAndKitTypeSchema } from '../../pick-ticket-items-schemas';

const disabled = false;

export function InventoryAndKitCombobox() {
  const [ref, setRef] = useState<HTMLInputElement | null>(null);
  const [isFocused, focus] = useBoolean();
  const [isEnter, enterState] = useBoolean();
  const form = usePickTicketsItemFormContext();
  const [current_value] = form.watch(['inventory_and_kit']);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);
  const fetchDefaultOptionsRef = useRef(true);

  const {
    mutateAsync: getKitOptions,
    data: kitsData,
    isPending: isLoadingKits,
  } = useGetInventoryKitWithItemsOnlyMutation({
    onSuccess: () => {
      focus.setFalse();
    },
  });

  const {
    mutateAsync: getInventoryOptions,
    data: inventoryData,
    isPending: isLoadingInventory,
  } = useGetInStockInventoriesMutation();

  const formatGroupLabel = useCallback((data: InventoryAndKitGroupedOption) => <div>{data.label}</div>, []);
  const getOptionLabel = useCallback((option: InventoryAndKitOptionType) => {
    return [isInventory(option) ? option?.primary_sku : option.sku, option?.name].filter((i) => !!i).join(' - ');
  }, []);
  const getOptionValue = useCallback((option: InventoryAndKitOptionType) => option?.id.toString(), []);

  const fetchOptions = useCallback(
    (search = '', per_page = 25) => {
      if ((!isFocused || disabled) && !search) {
        return;
      }
      getKitOptions({ search, per_page, include: 'category,inventory_kit_items.inventory.inventory_category' });
      getInventoryOptions({
        search,
        per_page,
        include: 'inventory_category',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, isFocused]
  );

  const updateValue = useCallback(
    (val: InventoryKitQuerySchema | InventoryQueryLinkerSchema | null, _: InventoryAndKitTypeSchema | null) => {
      form.setValue('inventory_and_kit', val, { shouldDirty: true });
    },
    [form]
  );

  const handleChange = useCallback(
    (v: InventoryAndKitOptionType | null) => {
      if (!v) {
        updateValue(null, null);
      } else if (isInventory(v)) {
        updateValue(v, 'inventory');
      } else if (isKit(v)) {
        updateValue(v, 'kit');
      }
    },
    [updateValue]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') {
        return;
      }
      enterState.setTrue();
    },
    [enterState]
  );

  const groupedOptions = useMemo(
    (): InventoryAndKitGroupedOption[] => [
      {
        label: 'Inventory',
        // We need to filter out the items that have no stock
        options: (inventoryData || []).filter((item) => (item?.current_stock ?? 0) > 0) || [],
      },
      {
        label: 'Inventory Kits',
        options: (kitsData || []).filter((item) => (item?.inventory_kit_items?.length ?? 0) > 0) || [],
      },
    ],
    [kitsData, inventoryData]
  );

  useEffect(() => {
    if (fetchDefaultOptionsRef.current) {
      fetchOptions();
      fetchDefaultOptionsRef.current = false;
    }
  }, [fetchOptions]);

  useEffect(() => {
    if (!fetchDefaultOptionsRef.current) {
      fetchOptions(debouncedQuery);
    }
  }, [fetchOptions, debouncedQuery]);

  useEffect(() => {
    if (!query || !isEnter) {
      return;
    }

    if (isLoadingKits || isLoadingInventory) {
      return;
    }

    enterState.setFalse();
    const [inv, kits] = groupedOptions;
    if (inv.options.length === 0 && kits.options.length === 1) {
      handleChange(kits.options[0]);
    } else if (kits.options.length === 0 && inv.options.length === 1) {
      handleChange(inv.options[0]);
    }
    ref?.blur();
  }, [enterState, groupedOptions, handleChange, isEnter, isLoadingInventory, isLoadingKits, query, ref]);

  return (
    <Select<InventoryAndKitOptionType, false, InventoryAndKitGroupedOption>
      label="Inventory Items"
      value={current_value}
      options={groupedOptions}
      formatGroupLabel={formatGroupLabel}
      onInputChange={setQuery}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      onChange={handleChange}
      onFocus={focus.setTrue}
      onBlur={focus.setFalse}
      onKeyDown={handleKeyDown}
      isLoading={isLoadingKits || isLoadingInventory}
      disabled={disabled}
      isClearable
      getRef={(e) => setRef(e)}
    />
  );
}
